import { createContext, useState, useContext, useCallback } from "react";
import {
  GeoLocationUrl,
  SupportedProductRegions,
  RegionToLanguageMaps,
} from "../constants";
import { fetchJSON, updateUrlParam } from "../services/utilitiesService";
import { useTranslation } from "react-i18next";

const UserContext = createContext();

// Export the hook to access the UID context
export const useUser = () => {
  return useContext(UserContext);
};

// Provider component to wrap around the app
export const UserProvider = ({ children }) => {
  const [uid, setUid] = useState(null);
  const [selectedPreferredCategory, setSelectedPreferredCategory] =
    useState(null);
  const [regionName, setRegionName] = useState(null);
  const [regionLanguages, setRegionLanguages] = useState(["en"]);
  const { i18n } = useTranslation();

  // Function to update the UID manually
  const updateUID = useCallback((newUID) => {
    setUid(newUID);
  }, []);

  const getRegion = async () => {
    if (regionName !== null) return;

    const geoResponse = await fetchJSON(GeoLocationUrl);
    if (
      Object.keys(geoResponse).length > 0 &&
      SupportedProductRegions.includes(geoResponse?.country_code2.toLowerCase())
    ) {
      const normalized = geoResponse.country_code2.toLowerCase();
      setRegionName((prev) => {
        if (prev !== null && prev === normalized) return prev;
        return normalized;
      });
      // get query param
      const params = new URLSearchParams(window.location.search);
      const requested = params.get("lng")?.toLowerCase();
      const initialLng = RegionToLanguageMaps[normalized]["allowed"].includes(
        requested,
      )
        ? requested
        : RegionToLanguageMaps[normalized]["default"];
      i18n.changeLanguage(initialLng);
      updateUrlParam("lng", initialLng);
      setRegionLanguages(RegionToLanguageMaps[normalized]["allowed"]);
    } else {
      setRegionName((prev) => {
        if (prev !== null && prev === "de") return prev;
        return "de";
      });
      i18n.changeLanguage("en");
      updateUrlParam("lng", "en");
      setRegionLanguages(["en"]);
    }
  };

  const updatePreferredCategory = useCallback((preferredCategory) => {
    setSelectedPreferredCategory(preferredCategory);
  }, []);

  return (
    <UserContext.Provider
      value={{
        uid,
        selectedPreferredCategory,
        regionName,
        regionLanguages,
        getRegion,
        updateUID,
        updatePreferredCategory,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
